import { useId, useLayoutEffect, useMemo, useRef } from 'react'
import { previewModelPath, validateModelPath, modelPathFieldKeys } from './modelPath'

/**
 * Le champ « Chemin public des contenus » d'un type — un seul, partagé.
 *
 * Trois écrans règlent ce même chemin : l'onglet « Modèle » du type (poser le
 * modèle, puis le modifier) et le panneau « Adresse » ouvert DEPUIS le modèle
 * dans l'éditeur de blocs. Le libellé, l'aide, la liste des champs citables et
 * le verdict de forme y sont les mêmes — d'où ce composant plutôt que trois
 * copies qui divergeraient.
 *
 * POURQUOI DU JSX (justification config-first) : ce champ vit dans l'éditeur
 * React du builder, dont l'état est la source de vérité ; il FACTORISE un
 * balisage déjà écrit deux fois, il n'ouvre pas une surface impérative de plus.
 * La grammaire du chemin, elle, est dans `modelPath.js` — miroir du back, qui
 * garde le verdict.
 */
export const ModelPathField = ({
  id,
  value,
  onChange,
  fields = [],
  siteName = '',
  disabled = false,
  label = 'Chemin public des contenus',
}) => {
  const fallbackId = useId()
  const fieldId = id || fallbackId
  const helpId = `${fieldId}-help`
  const tokensId = `${fieldId}-tokens`
  const inputRef = useRef(null)

  const keys = useMemo(() => fields.map(f => f.key), [fields])
  const error = validateModelPath(value, keys)
  // L'aperçu parle du champ RÉELLEMENT cité, pas d'un « mon-contenu »
  // générique : c'est ce qui rend le @ lisible d'un coup d'œil.
  const cited = modelPathFieldKeys(value)
  const sampleData = Object.fromEntries(
    cited.map(key => [key, fields.find(f => f.key === key)?.label || key]),
  )
  const example = error ? '' : previewModelPath(value, { data: sampleData })

  // Où replacer le curseur au prochain rendu. Posé en `useLayoutEffect` et non
  // dans un `requestAnimationFrame` : l'animation frame arrive APRÈS le commit
  // de React, donc parfois après les premières frappes suivantes — le curseur
  // sautait en plein mot (« @titleerya — Em » au lieu de « @title — Emerya »).
  const pendingCaret = useRef(null)
  useLayoutEffect(() => {
    const pos = pendingCaret.current
    if (pos == null) return
    pendingCaret.current = null
    const input = inputRef.current
    if (!input) return
    input.focus()
    input.setSelectionRange(pos, pos)
  })

  /** Insère `@clé` là où est le curseur — le champ garde le focus. */
  const insertToken = (key) => {
    const input = inputRef.current
    const token = `@${key}`
    if (!input) {
      onChange(`${value}${token}`)
      return
    }
    const start = input.selectionStart ?? value.length
    const end = input.selectionEnd ?? value.length
    onChange(value.slice(0, start) + token + value.slice(end))
    pendingCaret.current = start + token.length
  }

  return (
    <div>
      <label htmlFor={fieldId} className="field-label">{label}</label>
      <input
        id={fieldId}
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value.trim())}
        aria-describedby={`${helpId} ${tokensId}`}
        aria-invalid={!!error}
        spellCheck={false}
        disabled={disabled}
        placeholder="/actualites/@titre"
        className="input w-full font-mono"
      />
      <p id={helpId} className={`text-xs mt-1 ${error ? 'text-red-600' : 'text-gray-500'}`}>
        {error || (
          <>
            Un contenu « {sampleData[cited[0]] || 'Mon contenu'} » sera servi à{' '}
            <span className="font-mono">{example}</span>
            {siteName ? <> sur le site {siteName}</> : null}.
          </>
        )}
      </p>
      <div id={tokensId} className="mt-2">
        {fields.length === 0 ? (
          <p className="text-xs text-gray-500">
            Ce type ne déclare aucun champ : son chemin ne peut pas être construit
            depuis ses contenus (onglet « Schéma »).
          </p>
        ) : (
          <TokenChips
            fields={fields}
            onInsert={insertToken}
            disabled={disabled}
            label="Champs citables dans le chemin"
            hint={
              <>
                L&apos;adresse se construit avec les champs du contenu : chaque{' '}
                <code className="font-mono">@champ</code> y met sa valeur, mise en forme
                d&apos;URL (accents et ponctuation retirés, espaces en tirets).
              </>
            }
          />
        )}
      </div>
    </div>
  )
}

/**
 * Les champs d'un type, en pastilles à insérer — partagé par le chemin public
 * et par les métadonnées, qui citent les mêmes champs avec le même `@`.
 *
 * Des BOUTONS plutôt qu'une liste : écrire « @ » puis la bonne clé suppose de
 * connaître le schéma par cœur. `onMouseDown` annulé pour que le champ visé ne
 * perde pas le focus au clic — sans quoi on ne saurait plus où insérer.
 */
export const TokenChips = ({ fields, onInsert, disabled = false, label, hint = null }) => (
  <>
    {hint && <p className="text-xs text-gray-500">{hint}</p>}
    <ul className="flex flex-wrap gap-1.5 mt-1.5" aria-label={label}>
      {fields.map(f => (
        <li key={f.key}>
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => onInsert(f.key)}
            disabled={disabled}
            className="btn btn-ghost btn-sm px-2 font-mono text-xs"
            title={f.label ? `${f.label} — insérer @${f.key}` : `Insérer @${f.key}`}
          >
            @{f.key}
          </button>
        </li>
      ))}
    </ul>
  </>
)
